import { faChevronLeft, faChevronRight, faLock } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ButtonAtom } from "../atom";
import "./InvoiceStepper.scss";

const InvoiceStepper = ({
    currentStep,
    steps,
    completedSteps,
    onStepClick,
    maxStepReached,
    onBack,
    onNext,
    nextDisabled,
    nextLabel,
    actions,
    disabledStepsMap = {}, // Map of stepNumber -> translation key for reason
    hideStepIndicators = false, // Hide step indicators for edit/view modes
    backLabel, // Custom back button label
}) => {
    const { t } = useTranslation();

    const getStepClassName = (stepNumber, isActive, currentStep, isLocked) => {
        const classes = ["invoice-stepper__step"];

        // Locked steps take priority - these are disabled due to business rules
        if (isLocked) {
            classes.push("invoice-stepper__step--locked");
        }
        // Active step always takes priority - should be blue and blinking
        else if (isActive) {
            classes.push("invoice-stepper__step--active");
        } else if (completedSteps.includes(stepNumber) && stepNumber < currentStep) {
            // Only show as completed if it's before the current step
            classes.push("invoice-stepper__step--completed");
        } else if (stepNumber <= maxStepReached) {
            classes.push("invoice-stepper__step--clickable");
        } else {
            classes.push("invoice-stepper__step--disabled");
        }

        return classes.join(" ");
    };

    const handleStepClick = (stepNumber) => {
        // Don't allow clicking locked steps
        if (disabledStepsMap[stepNumber]) {
            return;
        }

        if (stepNumber <= maxStepReached && onStepClick) {
            onStepClick(stepNumber);
        }
    };

    // Check if back button should be disabled
    const isBackDisabled = () => {
        if (currentStep === 1) { return true; }

        // Check if the previous step is locked
        const prevStep = currentStep - 1;
        return !!disabledStepsMap[prevStep];
    };

    return (
        <div className="invoice-stepper-container">
            {/* Left: Back button */}
            <div className="invoice-stepper-container__left">
                {onBack && (
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            isBackDisabled() && disabledStepsMap[currentStep - 1] ? (
                                <Tooltip id="back-button-tooltip">
                                    {t(disabledStepsMap[currentStep - 1])}
                                </Tooltip>
                            ) : (
                                <></>
                            )
                        }
                    >
                        <span className="d-inline-block">
                            <ButtonAtom
                                className={`btn btn-outline-secondary${isBackDisabled() ? " pe-none" : ""}`}
                                onClick={onBack}
                                disabled={isBackDisabled()}
                            >
                                <FontAwesomeIcon icon={faChevronLeft} className="me-1" />
                                {backLabel || t("common.back")}
                            </ButtonAtom>
                        </span>
                    </OverlayTrigger>
                )}
            </div>

            {/* Center: Stepper */}
            {!hideStepIndicators && (
                <div className="invoice-stepper">
                    {steps.map((step, index) => {
                        const stepNumber = index + 1;
                        // Determine if this is the active step
                        // Handle both cases: all 3 steps shown, or step 2 skipped (only 2 steps)
                        let isActive;
                        if (steps.length === 3) {
                            // Normal case: step matches current step
                            isActive = stepNumber === currentStep;
                        } else if (steps.length === 2) {
                            // Step 2 skipped: map currentStep to display position
                            // currentStep 1 -> display position 1 (index 0)
                            // currentStep 3 -> display position 2 (index 1)
                            isActive = (currentStep === 1 && index === 0) || (currentStep === 3 && index === 1);
                        } else {
                            isActive = false;
                        }

                        const isLocked = !!disabledStepsMap[stepNumber];
                        const isClickable = stepNumber <= maxStepReached && !isLocked;
                        const disabledReasonKey = disabledStepsMap[stepNumber];
                        const disabledReason = disabledReasonKey ? t(disabledReasonKey) : undefined;

                        const stepElement = (
                            <div
                                className={`${getStepClassName(stepNumber, isActive, currentStep, isLocked)} ${isLocked ? "is-step--locked" : isClickable ? "is-step--clickable" : "is-step--default"}`}
                                onClick={() => handleStepClick(stepNumber)}
                                role="button"
                                tabIndex={isClickable ? 0 : -1}
                                aria-current={isActive ? "step" : undefined}
                                aria-disabled={!isClickable}
                                onKeyDown={(e) => {
                                    if (
                                        (e.key === "Enter" || e.key === " ") &&
                                        isClickable
                                    ) {
                                        handleStepClick(stepNumber);
                                    }
                                }}
                            >
                                <div className="invoice-stepper__step-number">
                                    {isLocked ? <FontAwesomeIcon icon={faLock} /> : stepNumber}
                                </div>
                                <div className="invoice-stepper__step-label">
                                    {t(step.label)}
                                </div>
                            </div>
                        );

                        return (
                            <React.Fragment key={stepNumber}>
                                {isLocked && disabledReason ? (
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip id={`step-${stepNumber}-tooltip`}>
                                                {disabledReason}
                                            </Tooltip>
                                        }
                                    >
                                        {stepElement}
                                    </OverlayTrigger>
                                ) : (
                                    stepElement
                                )}
                                {index < steps.length - 1 && (
                                    <div className="invoice-stepper__connector" />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            )}

            {/* Right: Actions */}
            <div className="invoice-stepper-container__right">
                {actions ? (
                    // Step 3: Show custom actions (Save, Toggle, Create Aufmass)
                    <div className="d-flex gap-2 align-items-center">
                        {actions}
                    </div>
                ) : onNext && (
                    // Steps 1-2: Show Next button
                    <ButtonAtom
                        variant="btn btn-primary"
                        onClick={onNext}
                        disabled={nextDisabled}
                    >
                        {nextLabel || t("common.next")}
                        <FontAwesomeIcon icon={faChevronRight} className="ms-1" />
                    </ButtonAtom>
                )}
            </div>
        </div>
    );
};

export default InvoiceStepper;
